<template>
  <div>
    <div class="item-select-methods button-group">
      <gl-button
        class="button-group__button"
        :primary="itemSelectMethod === 'keyword'"
        @click="toggleItemSelect('keyword')"
        >あいまい検索</gl-button
      >
      <gl-button
        class="button-group__button"
        :primary="itemSelectMethod === 'code'"
        @click="toggleItemSelect('code')"
        >コード入力</gl-button
      >
      <item-select-by-file
        class="button-group__button"
        :company="company"
        @update:errors="val => (error = val)"
        @selected="onSelectByFile"
      />
    </div>
    <div v-if="itemGroupId === undefined">
      <div class="items-container" :class="{ 'no-item': items.length === 0 }">
        <item-list
          :items="items"
          @update:items="$emit('update:items', $event)"
          deletable
        />
      </div>
      <div class="actions button-group">
        <div class="left"></div>
        <span
          class="item-count"
          :class="{ 'exceeds-limit': items.length > maxItems }"
          >{{ itemCount }} / {{ maxItems }} 商品</span
        >
        <gl-button class="button-group__button" raised @click="removeAll"
          >全削除</gl-button
        >
      </div>
    </div>
    <div v-else class="items-container">
      <div class="items-container-child">
        <p>
          ファイルアップロード、若しくは他分析からの遷移により商品選択済です。
        </p>
        <blockquote>
          <div>
            <a
              href="#"
              class="download-link animated infinite"
              :class="{ flash: downloadingCsv }"
              @click.prevent="downloadCsv"
            >
              CSVファイルをダウンロード
            </a>
            して選択中商品を確認できます。
          </div>
          <div>
            ダウンロードしたCSVファイルを編集し再アップロードすることもできます。
          </div>
          <div>
            CSVファイルを編集して商品を追加する場合、商品名を入力する必要はありません。
          </div>
        </blockquote>
      </div>
    </div>

    <!-- 商品検索 -->
    <md-dialog
      class="item-select-dialog"
      :md-active="!!itemSelectComponent"
      @md-clicked-outside="itemSelectMethod = undefined"
    >
      <md-dialog-content>
        <component
          :is="itemSelectComponent"
          :company="company"
          @selected="addItems"
        />
        <gl-button
          class="button-dialog-close"
          icon-button
          @click="itemSelectMethod = undefined"
          ><md-icon>cancel</md-icon></gl-button
        >
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<script>
import { isArray } from 'lodash'

import ItemList from '@/components/ItemList.vue'
import ItemSelectByKeyword from '@/components/ItemSelectByKeyword'
import ItemSelectByCode from '@/components/ItemSelectByCode'
import ItemSelectByFile from '@/components/ItemSelectByFile2'
import ErrorReport from '@/mixins/ErrorReport'

const MAX_ITEMS = process.env.VUE_APP_MAX_ITEMS
  ? Number(process.env.VUE_APP_MAX_ITEMS)
  : 500

/**
 * 商品選択コンポーネント
 */
export default {
  components: {
    ItemList,
    ItemSelectByKeyword,
    ItemSelectByCode,
    ItemSelectByFile,
  },
  mixins: [ErrorReport],
  props: {
    /**
     * 企業コード
     */
    company: {
      type: String,
      required: true,
    },
    /**
     * 分析対象商品一覧
     */
    items: {
      type: Array,
      default: () => [],
    },
    itemGroupId: {
      type: String,
    },
  },
  data() {
    return {
      /**
       * 選択可能な最大商品数
       */
      maxItems: MAX_ITEMS,
      /**
       * 商品選択方式
       *
       * 曖昧検索や商品コードでの検索などを指定します。通常は検索用コンポーネントを
       * 子コンポーネントとして、子は`selected`イベントを発行することでこのコンポーネント
       * の選択商品への追加を行ってもらいます。なので、検索用コンポーネントは`selected`
       * イベントで商品リストを送りさえすれば、このコンポーネントの商品検索用コンポーネント
       * として成り立ちます。
       */
      itemSelectMethod: undefined,

      /**
       * 前回選択企業
       */
      lastCompany: undefined,
      /**
       * CSVファイルのダウンロード中フラグ
       */
      downloadingCsv: false,
      /**
       * 入力エラー
       */
      error: [],
    }
  },
  computed: {
    /**
     * 商品選択コンポーネント
     *
     * 商品選択方式に対応する商品選択コンポーネントを取得します。
     * 画面描画のいらないコンポーネント（e.g. ファイル入力）はここで対応する
     * 必要はありません。
     */
    itemSelectComponent() {
      switch (this.itemSelectMethod) {
        case 'keyword':
          return ItemSelectByKeyword
        case 'code':
          return ItemSelectByCode
      }
      return undefined
    },
    /**
     * 選択されている商品数
     */
    itemCount() {
      return this.items.length
    },
    errors() {
      if (this.itemGroupId) {
        return []
      }

      const errors = [...this.error]

      if (this.items.length === 0) {
        errors.push('商品を選択してください')
      }

      if (this.items.length > this.maxItems) {
        errors.push(`選択できる商品数は${this.maxItems}商品までです`)
      }

      return errors
    },
  },
  methods: {
    /**
     * 商品選択コンポーネント切替
     */
    toggleItemSelect(method) {
      if (this.itemSelectMethod === method) {
        this.itemSelectMethod = undefined
      } else {
        this.itemSelectMethod = method
      }
    },
    /**
     * 商品一覧クリア
     */
    removeAll() {
      this.$emit('update:items', [])
      this.$emit('update:itemGroupId', undefined)
    },
    /**
     * 「ファイル読込」からの商品選択イベント受信
     */
    onSelectByFile(data) {
      if (isArray(data)) {
        this.addItems(data)
      } else {
        this.setItemGroupId(data)
      }
    },
    /**
     * 商品の追加
     */
    addItems(items) {
      const newItemCodes = items.map(i => i.code)

      // TODO パフォーマンスで課題が出たら修正
      this.$emit('update:items', [
        ...this.items.filter(i => !newItemCodes.includes(i.code)),
        ...items,
      ])
      this.itemSelectMethod = undefined
      this.$emit('update:itemGroupId', undefined)
    },
    /**
     * リソースIDを設定する
     */
    setItemGroupId(id) {
      this.$emit('update:items', [])
      this.$emit('update:itemGroupId', id)
      this.itemSelectMethod = undefined
    },
    async downloadCsv() {
      this.downloadingCsv = true

      try {
        const url = await this.$repositories
          .get('item')
          .downloadCsvByItemGroupId(this.itemGroupId)
        window.open(url)
      } finally {
        this.downloadingCsv = false
      }
    },
  },
  watch: {
    company: {
      immediate: true,
      async handler(company) {
        if (company && this.lastCompany && company !== this.lastCompany) {
          this.$emit('update:items', [])
        }
        this.lastCompany = company
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.items-container {
  position: relative;
  height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.items-container-child {
  margin-left: var(--spacing-medium);
  margin-right: var(--spacing-medium);
}

.no-item {
  background: rgba(0, 0, 0, 0.12);

  &::after {
    font-family: 'Material Icons';
    content: 'mood_bad NO ITEM';
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    font-size: 58px;
    color: rgba(0, 0, 0, 0.12);
  }
}

.item-select-dialog {
  min-width: 700px;
}

.button-dialog-close {
  position: fixed;
  top: 6px;
  right: 0;
}

.item-select-methods,
.item-select,
.items-container {
  margin-top: var(--spacing-medium);
}

.item-select {
  padding-left: calc(var(--spacing-large) * 2);
}

.actions {
  width: 100%;
  text-align: right;
  display: flex;
  align-items: center;

  .left {
    flex: 1;
  }
}

.item-count {
  margin-right: var(--spacing-medium);
}

.exceeds-limit {
  color: var(--color-warn);
}

blockquote {
  font-size: var(--font-size-small);
  border-left: 3px solid var(--color-border);
  margin-left: 0;
  padding-left: var(--spacing-medium);
  padding-top: var(--spacing-small);
  padding-bottom: var(--spacing-small);
}

.download-link {
  display: inline-block;
  &.animated.flash {
    color: var(--color-secondary-text);
    pointer-events: none;
  }
}
</style>
